// File: src/store/commentsStore.ts
import { create } from "zustand";
import { IComments, SortOption } from "@/types/CommentsTypes";
import { uniqueId } from "lodash-es";
import { IUsers } from "@/types";
import { safeT } from "@/utils/safeT";
import { useTranslations } from "next-intl";

// TODO: replace with real API calls
// const dummyComments = (): IComments[] => [
//   {
//     id: uniqueId() + "",
//     debateId: "debate-1",
//     lang: "ko",
//     parentId: null,
//     userId: "user-1",
//     content: "저도 찬성합니다! 지자체가 더 많은 권한을 가져야 지역 주민의 삶이 개선될 거예요.",
//     stance: "pros",
//     likeCount: 10,
//     dislikeCount: 0,
//     liked: false,
//     disliked: false,
//     createdAt: "2025-04-10T12:00:00Z",
//     updatedAt: "2025-04-10T12:00:00Z",
//     deletedAt: null,
//     nickname: "HappyDay",
//     replies: [
//       {
//         id: uniqueId() + "",
//         debateId: "debate-1",
//         lang: "ko",
//         parentId: null,
//         userId: "user-1",
//         content: "저도 찬성합니다! 지자체가 더 많은 권한을 가져야 지역 주민의 삶이 개선될 거예요.",
//         stance: "pros",
//         likeCount: 10,
//         dislikeCount: 0,
//         liked: false,
//         disliked: false,
//         createdAt: "2025-04-10T12:00:00Z",
//         updatedAt: "2025-04-10T12:00:00Z",
//         deletedAt: null,
//         nickname: "BrightStar",
//         replies: [],
//       },
//       {
//         id: uniqueId() + "",
//         debateId: "debate-1",
//         lang: "ko",
//         parentId: null,
//         userId: "user-1",
//         content: "저도 동의합니다. 지역 주민의 의견이 더 잘 반영될 것 같아요.",
//         stance: "pros",
//         likeCount: 10,
//         dislikeCount: 0,
//         liked: false,
//         disliked: false,
//         createdAt: "2025-04-10T12:00:00Z",
//         updatedAt: "2025-04-10T12:00:00Z",
//         deletedAt: null,
//         nickname: "HappyDay",
//         replies: [],
//       },
//       {
//         id: uniqueId() + "",
//         debateId: "debate-1",
//         lang: "ko",
//         parentId: null,
//         userId: "user-1",
//         content: "맞아요! 지역별 특성을 반영한 정책이 더 효과적일 것 같아요.",
//         stance: "pros",
//         likeCount: 10,
//         dislikeCount: 0,
//         liked: false,
//         disliked: false,
//         createdAt: "2025-04-10T12:00:00Z",
//         updatedAt: "2025-04-10T12:00:00Z",
//         deletedAt: null,
//         nickname: "Thinker",
//         replies: [],
//       },
//     ],
//   },
//   {
//     id: uniqueId() + "",
//     debateId: "debate-1",
//     lang: "ko",
//     parentId: null,
//     userId: "user-1",
//     content: "저도 동의합니다. 지역 주민의 의견이 더 잘 반영될 것 같아요.",
//     stance: "pros",
//     likeCount: 10,
//     dislikeCount: 0,
//     liked: false,
//     disliked: false,
//     createdAt: "2025-04-10T12:00:00Z",
//     updatedAt: "2025-04-10T12:00:00Z",
//     deletedAt: null,
//     nickname: "BrightStar",
//     replies: [],
//   },
//   {
//     id: uniqueId() + "",
//     debateId: "debate-1",
//     lang: "ko",
//     parentId: null,
//     userId: "user-1",
//     content: "맞아요! 지역별 특성을 반영한 정책이 더 효과적일 것 같아요.",
//     stance: "pros",
//     likeCount: 10,
//     dislikeCount: 0,
//     liked: false,
//     disliked: false,
//     createdAt: "2025-04-10T12:00:00Z",
//     updatedAt: "2025-04-10T12:00:00Z",
//     deletedAt: null,
//     nickname: "Thinker",
//     replies: [],
//   },
//   // ...other dummy comments
// ];

type TActionType = "edit" | "new" | "delete" | "reply" | "mention";

interface CommentsState {
  comment?: IComments;
  user?: IUsers;
  sortOption: SortOption;
  limit: number;
  openReplies: Record<string, boolean>;
  commentInput: string;
  inputHeight: number;
  inputPlaceholder: string;
  refreshTrigger: number;
  actionType: TActionType;
  setSortOption: (option: SortOption) => void;
  toggleReplies: (id: string) => void;
  isOpenReplies: (id: string) => boolean;
  setUser: (value?: IUsers) => void;
  setComment: (value?: IComments) => void;
  setCommentInput: (value: string) => void;
  triggerRefresh: () => void;
  setInputHeight: (value: number) => void;
  setInputPlaceholder: (value: string) => void;
  focusInput: () => void;
  setFocusInput: (fn: () => void) => void;
  setActionType: (t: ReturnType<typeof useTranslations>, value: TActionType) => void;
}

export const useCommentsStore = create<CommentsState>((set, get) => ({
  sortOption: "popular",
  openReplies: {},
  commentInput: "",
  inputHeight: 0,
  limit: 20,
  refreshTrigger: 0,
  inputPlaceholder: "",
  actionType: "new",
  setSortOption: (option) => {
    if (get().sortOption !== option) {
      set({ sortOption: option });
    }
  },

  toggleReplies: (id) => {
    set((state) => ({
      openReplies: { ...state.openReplies, [id]: !state.openReplies[id] },
    }));
  },
  isOpenReplies: (id) => get().openReplies[id],
  setUser: (value: IUsers | undefined) => set({ user: value }),
  setComment: (value: IComments | undefined) => set({ comment: value }),
  setCommentInput: (value) => set({ commentInput: value }),
  setInputHeight: (value) => set({ inputHeight: value }),
  setInputPlaceholder: (value) => set({ inputPlaceholder: value }),
  setActionType: (t: ReturnType<typeof useTranslations>, value: TActionType) =>
    set((state) => {
      let placeholder = "";
      switch (value) {
        case "new":
          placeholder = safeT(t, "ui.placeholder.commentPrompt", {
            defaultMessage: "의견을 남겨주세요",
          });
          break;
        case "reply":
          placeholder = safeT(t, "ui.placeholder.addReply", {
            defaultMessage: "답글 추가",
          });
          break;
        case "mention":
          placeholder = `@${get().user?.nickname} ${safeT(t, "ui.placeholder.addReply", {
            defaultMessage: "답글 추가",
          })}`;
          break;
        default:
          break;
      }
      return {
        actionType: value,
        inputPlaceholder: placeholder,
      };
    }),
  focusInput: () => {},
  setFocusInput: (fn) => set({ focusInput: fn }),
  triggerRefresh: () =>
    set((state) => ({
      refreshTrigger: state.refreshTrigger + 1,
    })),
}));
